/**
 * Same-scene dated history → estimator inputs (issue #69).
 *
 * The rate estimator (cleanup-estimator.ts) is pure and honest — but
 * explore.tsx fed it `sceneObservations: []`, so Tiers A/B were
 * structurally unreachable and every popup degraded to "Not enough
 * data yet". This adapter builds the estimator's inputs from what a
 * feature ACTUALLY carries, per docs/cleanup-ledger-spec.md, without
 * ever fabricating a date:
 *
 *  - OLM enriched rows (`date_basis: 'photo_dates'`): observed_first →
 *    observed_at is a REAL dated accumulation window (earliest/latest
 *    matched photo), and `_display.tag_breakdown.total_items` (falling
 *    back to `quantity`) is the item count accumulated across it. The
 *    window is encoded as its two endpoints — zero items PHOTOGRAPHED
 *    at window-open, the full count at window-close — so the
 *    estimator's Tier A computes exactly the spec's "rate band =
 *    quantity/weeks ± 30%". The zero is the window-start anchor of that
 *    accumulation reading (no photos had been taken yet), not a claim
 *    the ground was clean.
 *  - Sweep-dated OLM rows (no `photo_dates` stamp): observed_at is the
 *    snapshot-ingest moment, not an observation — the time-window.ts
 *    honesty rule counts these as UNDATED, so they contribute NO dated
 *    points and the estimator degrades to Tier D honestly.
 *  - dc311 rows: `_display.repeat_count` is a frequency SIGNAL, but it
 *    is a scalar with a single ADDDATE — no per-report dates. The
 *    estimator contract computes Tier C only from ≥2 DATED events
 *    (recent-half vs prior-half), which a dateless count cannot feed —
 *    so dc311 maps to a single dated point (graceful Tier D skip),
 *    never a forced trend. The repeat-count story stays where it
 *    already lives: dc311-popup.ts's "Reported N× …" line.
 *
 * Shared-lib module (no DOM/Leaflet): the legacy Leaflet path wires it
 * in explore.tsx; the MapLibre tile-mode path (explore-map-tiles.ts)
 * can adopt it unchanged when its popups grow a ledger.
 */

import type {
  DatedCleanupObservation,
  DatedSceneObservation,
  EstimatorInput,
} from './cleanup-estimator';
import { realObservedAtMs, realObservedFirstMs } from './time-window';

/** Minimal structural feature shape (same looseness as obs-popup.ts). */
interface FeatureLike {
  properties?: unknown;
}

/**
 * The item count the feature itself carries — `_display.tag_breakdown.
 * total_items` first, then `quantity` — the same precedence the popup's
 * itemsRemainingEstimate already uses (explore.tsx). Null when the
 * record was never counted; never a fabricated default.
 */
export function featureItemCount(f: FeatureLike): number | null {
  const p = (f.properties ?? {}) as Record<string, any>;
  const total = p._display?.tag_breakdown?.total_items;
  const n = typeof total === 'number' ? total : typeof p.quantity === 'number' ? p.quantity : null;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

/**
 * Dated same-scene observation points for one feature. Empty for
 * undated records (fabricated-snapshot OLM, never-dated legacy rows) —
 * the date-honesty rule is delegated to time-window.ts's
 * realObservedAtMs/realObservedFirstMs, the same helpers the map's
 * window filter and vividness already trust, so "what counts as dated"
 * can never diverge between the filter and the estimator.
 */
export function sceneObservationsForFeature(f: FeatureLike): DatedSceneObservation[] {
  const endMs = realObservedAtMs(f);
  if (endMs === null) return []; // undated → no history; degrade, never fabricate
  const items = featureItemCount(f);
  const end: DatedSceneObservation = {
    observed_at: new Date(endMs).toISOString(),
    items_estimate: items,
  };
  const startMs = realObservedFirstMs(f);
  // A real accumulation window needs a distinct dated start AND a count
  // to spread across it; otherwise this is a single dated point (the
  // estimator's Tier D "second look needed" state).
  if (items === null || startMs === null || startMs >= endMs) return [end];
  return [{ observed_at: new Date(startMs).toISOString(), items_estimate: 0 }, end];
}

/**
 * The feature's logged cleanup events (backend `recent_cleanups`, PR
 * #28 read path) as estimator inputs. Tolerates the field being absent
 * or malformed — the live Lambda may predate the read-path deploy.
 */
export function cleanupObservationsForFeature(f: FeatureLike): DatedCleanupObservation[] {
  const rc = ((f.properties ?? {}) as Record<string, any>).recent_cleanups;
  if (!Array.isArray(rc)) return [];
  return rc
    .filter(
      (e): e is { cleaned_at: string; items_estimate: number } =>
        !!e && typeof e.cleaned_at === 'string' && typeof e.items_estimate === 'number',
    )
    .map((e) => ({ cleaned_at: e.cleaned_at, items_estimate: e.items_estimate }));
}

/** One-stop ledger input for a single stable observation (never a merged mound). */
export function estimatorInputForFeature(f: FeatureLike): EstimatorInput {
  return {
    cleanupEvents: cleanupObservationsForFeature(f),
    sceneObservations: sceneObservationsForFeature(f),
  };
}
